import styled from 'styled-components'
import { em } from 'polished'

import { BoxBase } from 'styles/components/layout'
import { Text } from 'shared/Typography'

const colorRegExp = /^(#|rgba|rgb).*/gi
const isColor = color => color && colorRegExp.test(color)
const getBackgroundImage = ({ path, ext, $src }, suffix) => (
  $src || `${path}${suffix}.${ext}`
)

export const Wrapper = styled.div`
  padding-top: ${em(32)};
  padding-bottom: ${em(32)};
  background-color: ${({ theme, $bg }) =>
    isColor($bg) ? $bg : theme.colors[$bg]};

  ${({ theme }) => theme.mq.upMd} {
    padding-top: ${em(48)};
    padding-bottom: ${em(56)};
  }
`

export const Container = styled(BoxBase)`
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto auto auto;
  grid-template-areas: 'header' 'illustration' 'content';
  gap: 0;
  color: ${({ theme, $color }) =>
    isColor($color) ? $color : theme.colors[$color]};

  ${({ theme }) => theme.mq.upMd} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    grid-template-areas: 'header header' 'illustration content';
    gap: ${em(50)} ${em(16)};
  }
`

export const Title = styled(Text)`
  grid-area: header;
  text-align: center;
  margin: 0 auto;

  ${({ theme }) => theme.mq.upMd} {
    max-width: ${({ isWgs, isBiome, isJuneBundle }) => {
      if (isJuneBundle && isWgs) return `${em(860, 50)}`
      return isWgs || isBiome ? `${em(455, 50)}` : '100%'
    }};
  }

  ${({ theme }) => theme.mq.upThreshold(1024)} {
    max-width: ${({ isWgs, isJuneBundle }) =>
      isJuneBundle && isWgs ? `${em(860, 50)}` : isWgs ? `${em(455, 50)}` : '100%'};
  }

  ${({ theme }) => theme.mq.downMd} {
    max-width: ${({ isWgs }) => isWgs ? `${em(320, 30)}` : `${em(260, 30)}`};
    margin-bottom: ${em(30, 30)};
  }
`

export const Content = styled.div`
  grid-area: content;
  margin-top: ${em(30)};

  ${({ theme }) => theme.mq.upMd} {
    margin-top: 0;
  }
`

export const OldPrice = styled(Text)`
  display: flex;
  align-items: center;
  grid-area: old-price;
`

export const PriceAndButton = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: ${em(20)};
  grid-area: price-button;
`

export const Price = styled(Text)`
`

export const SplitBadgeWrapper = styled.div`
  margin-top: ${em(16)};

  ${({ theme }) => theme.mq.upThreshold(1600)} {
    align-self: center;
    grid-area: split-badge;
    justify-self: start;
    margin-top: 0;
  }
`

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0 ${em(24)};
  margin-top: ${em(30)};

  ${({ theme }) => theme.mq.upThreshold(1600)} {
    align-items: center;
    column-gap: ${em(80)};
    display: grid;
    grid-template-areas:
      'old-price .'
      'price-button split-badge';
    grid-template-columns: max-content minmax(0, 1fr);
    row-gap: 0;
  }

  > button > span > span {
    font-family: inherit;
  }
  > button > div:first-of-type {
    background: ${({ theme, isEvent, buttonBg }) => isEvent && (theme.colors[buttonBg] || buttonBg)};
  }
`

export const Event = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

export const BadgeBase = styled.div`
  border-radius: 5px;
  line-height: 1;
  padding: ${em(8)} ${em(12)};
`

export const DiscountBadge = styled(BadgeBase)`
  background: ${({ theme, bg }) => (theme.colors[bg] || bg)};
  color: ${({ theme, color }) => (theme.colors[color] || color)};
  font-family: ${({ theme }) =>theme.typography.mediumFont};
  border: ${({ color, bg, theme }) => bg === 'transparent' ? `${(theme.colors[color] || color)} 1px solid` : 'none' };
`

export const EventLabel = styled(BadgeBase)`
  background: ${({ theme, bg }) => (theme.colors[bg] || bg)};
  color: ${({ theme, color }) => (theme.colors[color] || theme.colors.white)};
`

export const ListItem = styled(Text)`
  ${p => p.theme.mq.downMd} {
    line-height: 20px;
  }
`

export const List = styled.ul`
  margin-bottom: ${em(26)};
  padding-left: 18px;

  ${ListItem} + ${ListItem} {
    margin-top: 14px;

    ${p => p.theme.mq.upMd} {
      margin-top: ${p => parseFloat(p.theme.indents.paragraphIndent)}em;
    }

    ${p => p.theme.mq.upLg} {
      margin-top: ${p => p.theme.indents.paragraphIndent};
    }
  }
`

export const Image = styled.div`
  grid-area: illustration;
  height: 100%;
  min-height: ${em(246)};
  background-image: url('${props => getBackgroundImage(props, '_mob')}');
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  ${p => p.customStylesMob};

  ${({ theme }) => theme.mq.isRetina} {
    background-image: url('${props => getBackgroundImage(props, '_mob@2x')}');
  }

  ${({ theme }) => theme.mq.upMd} {
    min-height: 0;
    background-image: url('${props => getBackgroundImage(props, '_tab')}');
    background-position: top;
    ${p => p.customStylesTab};
  }

  ${({ theme }) => theme.mq.upMd2x} {
    background-image: url('${props => getBackgroundImage(props, '_tab@2x')}');
  }

  ${({ theme }) => theme.mq.upLg} {
    background-image: url('${props => getBackgroundImage(props, '_desktop@2x')}');
    //background-image: url('${({ path, ext }) => `${path}_desktop.${ext}`}');
    ${p => p.customStylesDesktop};
  }

  ${({ theme }) => theme.mq.upLg2x} {
    //background-image: url('${({ path, ext }) =>
      `${path}_desktop@2x.${ext}`}');
  }
  ${p => p.customStyles};
`
